
import React, { useState } from 'react';
import { Church, Missionary } from './types';
import { useLanguage } from '../../../contexts/language';
import { Church as ChurchIcon, ExternalLink, Info } from 'lucide-react';
import { 
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';

interface MissionaryTeamProps {
  church: Church;
  missionaries: Missionary[];
}

const MissionaryTeam: React.FC<MissionaryTeamProps> = ({ church, missionaries }) => {
  const { t } = useLanguage();
  const [selectedMissionary, setSelectedMissionary] = useState<Missionary | null>(null);
  
  const handleWebsiteClick = (website: string, event: React.MouseEvent) => {
    // Prevent the popover from closing when clicking the link
    event.stopPropagation();
    // Open website in a new tab
    window.open(website, '_blank', 'noopener,noreferrer');
  };
  
  return (
    <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
      <div className="flex items-center mb-2">
        <ChurchIcon className="h-4 w-4 text-blue-600 mr-2" />
        <h3 className="font-semibold text-blue-800">{church.name}</h3>
      </div>
      <h4 className="text-sm font-medium text-blue-700 mb-2">{t('missionaryTeam')}</h4>
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {missionaries.map(missionary => (
          <div 
            key={missionary.id} 
            className="bg-white p-2 rounded border border-blue-100 text-sm relative"
          >
            <div className="font-medium">{missionary.name}</div>
            <div className="text-xs text-gray-600 flex justify-between">
              <span>{missionary.origin_country}</span>
              <span>{t('yearsOfService')}: {missionary.years_of_service}</span>
            </div>
            
            {/* Action buttons */}
            <div className="flex justify-end mt-1 gap-1">
              {missionary.bio && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="text-blue-600 hover:text-blue-800 p-1 rounded">
                      <Info size={14} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3">
                    <div className="font-medium text-sm mb-1">{missionary.name}</div>
                    <p className="text-xs text-gray-700">{missionary.bio}</p>
                  </PopoverContent>
                </Popover>
              )}
              
              {missionary.website && (
                <button 
                  className="text-blue-600 hover:text-blue-800 p-1 rounded flex items-center text-xs gap-1"
                  onClick={(e) => handleWebsiteClick(missionary.website!, e)}
                >
                  <ExternalLink size={14} />
                  <span>{t('visitWebsite')}</span>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MissionaryTeam;
